/**
 * useChartZoom — x-axis zoom for Chart.js line charts.
 *
 * Purpose: let a user zoom into the part of the x axis under the mouse; the
 *   y axes rescale their top to the points inside the visible x window, and an
 *   axis that showed 0 before zooming keeps 0 as its baseline.
 *
 * Gestures (mouse only — touch keeps native page scrolling):
 *   - Drag across the plot     → zoom to the dragged x range
 *   - Shift + scroll wheel     → zoom in/out around the cursor's x position
 *     (not Ctrl: Ctrl + scroll is the browser's own page zoom, and a plain
 *     wheel must stay free to scroll the page past the chart)
 *   - Shift + drag (zoomed)    → pan left/right
 *   - Double-click             → reset
 *
 * One hook can drive several charts that share an x axis (pass the same
 * `plugin` to each): a gesture on any of them zooms them all.
 *
 * Controlled mode (pass `fullExtent` + `onRangeChange`, and `range`): the
 * caller owns the window — e.g. it slices or re-samples its data to the
 * window and sets the x min/max itself — and the hook only turns gestures
 * into proposed windows. Don't pass the range through applyZoomToOptions in
 * that mode; the caller's own options already show it.
 *
 * Usage (or just <LineChart zoomable /> — see LineChart.js):
 *   const zoom = useChartZoom({ resetKey: data });
 *   const zoomedOptions = useMemo(() => applyZoomToOptions(options, zoom.range), [options, zoom.range]);
 *   <Line data={data} options={zoomedOptions} plugins={[zoom.plugin]} />
 *   {zoom.isZoomed && <Button onClick={zoom.reset}>Reset zoom</Button>}
 *
 * Returns:
 *   - plugin: stable Chart.js plugin instance (react-chartjs-2 only reads
 *     `plugins` when the chart is created, so it must never change identity)
 *   - range: { min, max } in x-scale units (index for a category axis, value
 *     for a linear axis) or null when showing everything
 *   - isZoomed, reset
 *
 * Why zoom lives in React state rather than mutating chart.options:
 *   react-chartjs-2 replaces chart.options whenever the caller's options memo
 *   changes, which would silently drop an imperative min/max. Folding the range
 *   into the options the caller passes makes it survive any re-render.
 *
 * The y rescale is Chart.js's own behaviour: with x min/max set, a dataset's
 * y limits only count points inside those bounds. When the zoom starts, the
 * plugin records which y axes included 0 (range.zeroAxes); applyZoomToOptions
 * keeps those anchored at 0 so only their top moves, and leaves axes that
 * never showed 0 (temperature, pressure) to fit freely.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD_PX = 6;
// Matches --accent-rgb (periwinkle); canvas can't read CSS variables.
const SELECTION_FILL = "rgba(155, 155, 210, 0.16)";
const SELECTION_EDGE = "rgba(155, 155, 210, 0.8)";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const isYScale = (id, scale) =>
  scale?.axis === "y" || /^y/i.test(id) || scale?.position === "left" || scale?.position === "right";

/**
 * Fold a zoom range into a Chart.js options object without mutating it.
 * y scales listed in range.zeroAxes (or every linear y scale, when the range
 * doesn't say) keep 0 in range via beginAtZero, so zoomed values still read
 * against a zero baseline; the max comes from the visible points only.
 * Negative data still extends the axis below 0. Log scales are left alone.
 */
export function applyZoomToOptions(options, range, { xScaleId = "x" } = {}) {
  if (!range || !options) return options;
  const scales = { ...(options.scales || {}) };
  const x = scales[xScaleId] || {};
  scales[xScaleId] = {
    ...x,
    min: range.min,
    max: range.max,
    // The window edges are arbitrary values (329.9 …); as forced ticks they
    // crowd out the round ones. Category axes ignore this option.
    ticks: { ...(x.ticks || {}), includeBounds: false },
  };
  Object.keys(scales).forEach(id => {
    if (id === xScaleId || !isYScale(id, scales[id])) return;
    if (scales[id].type === "logarithmic") return;
    if (Array.isArray(range.zeroAxes) && !range.zeroAxes.includes(id)) return;
    scales[id] = { ...scales[id], beginAtZero: true };
  });
  // Animating every wheel tick feels laggy; snap while zoomed.
  return { ...options, animation: false, scales };
}

export default function useChartZoom({
  resetKey,
  xScaleId = "x",
  minRange = 2,
  enabled = true,
  // Controlled mode — see the header comment.
  range: controlledRange,
  onRangeChange,
  fullExtent: controlledFull,
} = {}) {
  const [ownRange, setRange] = useState(null);
  const controlled = typeof onRangeChange === "function";
  const range = controlled ? (controlledRange ?? null) : ownRange;

  // Latest values for the (stable) plugin closures.
  const rangeRef = useRef(null);
  const fullRef = useRef(null);
  const enabledRef = useRef(enabled);
  const minRangeRef = useRef(minRange);
  const onChangeRef = useRef(onRangeChange);
  const controlledFullRef = useRef(controlledFull);
  rangeRef.current = range;
  enabledRef.current = enabled;
  minRangeRef.current = minRange;
  onChangeRef.current = onRangeChange;
  controlledFullRef.current = controlled ? controlledFull : null;

  const commit = useCallback((next) => {
    if (next && fullRef.current) next = { ...next, zeroAxes: fullRef.current.zeroAxes };
    rangeRef.current = next;
    if (!next) fullRef.current = null;
    if (onChangeRef.current) onChangeRef.current(next);
    else setRange(next);
  }, []);

  const reset = useCallback(() => commit(null), [commit]);

  // New data (or a different chart) → back to the full extent. A controlled
  // caller owns its window, including when to clear it.
  useEffect(() => { if (!controlled) reset(); }, [resetKey, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  const pluginRef = useRef(null);
  if (!pluginRef.current) {
    const listeners = new WeakMap(); // chart → { handlers, drag, pan }

    // Full data extent: captured from the unzoomed scales the first time the
    // user zooms, held until reset — along with which y axes showed 0.
    const fullExtent = (chart) => {
      const scale = chart.scales[xScaleId];
      if (!scale) return null;
      // Controlled: the caller knows the full extent; the scale only shows
      // the current window.
      if (controlledFullRef.current) {
        fullRef.current = { ...controlledFullRef.current, zeroAxes: undefined };
        return fullRef.current;
      }
      if (!rangeRef.current || !fullRef.current) {
        const zeroAxes = Object.values(chart.scales)
          .filter(s => s.id !== xScaleId && s.axis === "y" && s.type !== "logarithmic" && s.min <= 0 && s.max >= 0)
          .map(s => s.id);
        fullRef.current = { min: scale.min, max: scale.max, zeroAxes };
      }
      return fullRef.current;
    };

    // Normalise a proposed window: enforce minRange, keep it inside the data,
    // and collapse to "no zoom" once it covers everything again.
    const setWindow = (chart, min, max) => {
      const full = fullExtent(chart);
      if (!full) return;
      const fullSpan = full.max - full.min;
      const span = Math.max(max - min, Math.min(minRangeRef.current, fullSpan));
      if (span >= fullSpan) { commit(null); return; }
      const mid = (min + max) / 2;
      let lo = mid - span / 2;
      lo = clamp(lo, full.min, full.max - span);
      commit({ min: lo, max: lo + span });
    };

    const inArea = (chart, x, y) => {
      const a = chart.chartArea;
      return a && x >= a.left && x <= a.right && y >= a.top && y <= a.bottom;
    };

    const localPos = (chart, evt) => {
      const rect = chart.canvas.getBoundingClientRect();
      return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
    };

    pluginRef.current = {
      id: "xZoom",

      afterInit(chart) {
        const canvas = chart.canvas;
        const state = { drag: null, pan: null, raf: 0 };

        const onWheel = (evt) => {
          if (!enabledRef.current || !evt.shiftKey) return;
          const { x, y } = localPos(chart, evt);
          if (!inArea(chart, x, y)) return;
          evt.preventDefault(); // stop the browser's shift+wheel horizontal scroll
          const scale = chart.scales[xScaleId];
          if (!scale) return;
          fullExtent(chart);
          const anchor = scale.getValueForPixel(x);
          // Exponential so a trackpad's small deltas zoom smoothly and a mouse
          // notch (~100) zooms ~20%.
          // Chrome/Edge/Firefox report a shift+wheel notch as deltaX (they
          // turn it into a horizontal scroll), Safari as deltaY — take either.
          const delta = evt.deltaY || evt.deltaX;
          const factor = clamp(Math.exp(delta * 0.0022), 0.5, 2);
          const lo = anchor - (anchor - scale.min) * factor;
          const hi = anchor + (scale.max - anchor) * factor;
          // Keep the anchor under the cursor: shift rather than recentre.
          const full = fullRef.current;
          const span = Math.max(hi - lo, Math.min(minRangeRef.current, full.max - full.min));
          if (span >= full.max - full.min) { commit(null); return; }
          const ratio = (anchor - scale.min) / (scale.max - scale.min || 1);
          let newLo = anchor - span * ratio;
          newLo = clamp(newLo, full.min, full.max - span);
          commit({ min: newLo, max: newLo + span });
        };

        const onMouseDown = (evt) => {
          if (!enabledRef.current || evt.button !== 0) return;
          const { x, y } = localPos(chart, evt);
          if (!inArea(chart, x, y)) return;
          const scale = chart.scales[xScaleId];
          if (!scale) return;
          evt.preventDefault(); // no text selection while dragging
          if (evt.shiftKey && rangeRef.current) {
            state.pan = { startX: x, min: scale.min, max: scale.max };
            canvas.style.cursor = "grabbing";
          } else {
            state.drag = { startX: x, curX: x };
          }
          window.addEventListener("mousemove", onWindowMove);
          window.addEventListener("mouseup", onWindowUp);
        };

        const onWindowMove = (evt) => {
          const { x } = localPos(chart, evt);
          const a = chart.chartArea;
          if (state.drag) {
            state.drag.curX = clamp(x, a.left, a.right);
            chart.draw();
          } else if (state.pan) {
            const { startX, min, max } = state.pan;
            const perPx = (max - min) / (a.right - a.left || 1);
            const shift = (startX - x) * perPx;
            cancelAnimationFrame(state.raf);
            state.raf = requestAnimationFrame(() => setWindow(chart, min + shift, max + shift));
          }
        };

        const onWindowUp = () => {
          window.removeEventListener("mousemove", onWindowMove);
          window.removeEventListener("mouseup", onWindowUp);
          if (state.drag) {
            const { startX, curX } = state.drag;
            state.drag = null;
            if (Math.abs(curX - startX) >= DRAG_THRESHOLD_PX) {
              const scale = chart.scales[xScaleId];
              fullExtent(chart);
              const a = scale.getValueForPixel(Math.min(startX, curX));
              const b = scale.getValueForPixel(Math.max(startX, curX));
              setWindow(chart, a, b);
            }
            chart.draw();
          }
          if (state.pan) {
            state.pan = null;
            canvas.style.cursor = "";
          }
        };

        const onHover = (evt) => {
          if (state.pan || !enabledRef.current) return;
          const { x, y } = localPos(chart, evt);
          canvas.style.cursor = inArea(chart, x, y) ? "crosshair" : "";
        };

        const onDblClick = (evt) => {
          if (!enabledRef.current || !rangeRef.current) return;
          const { x, y } = localPos(chart, evt);
          if (inArea(chart, x, y)) commit(null);
        };

        canvas.addEventListener("wheel", onWheel, { passive: false });
        canvas.addEventListener("mousedown", onMouseDown);
        canvas.addEventListener("mousemove", onHover);
        canvas.addEventListener("dblclick", onDblClick);

        listeners.set(chart, {
          state,
          detach: () => {
            canvas.removeEventListener("wheel", onWheel);
            canvas.removeEventListener("mousedown", onMouseDown);
            canvas.removeEventListener("mousemove", onHover);
            canvas.removeEventListener("dblclick", onDblClick);
            window.removeEventListener("mousemove", onWindowMove);
            window.removeEventListener("mouseup", onWindowUp);
            cancelAnimationFrame(state.raf);
          },
        });
      },

      afterDraw(chart) {
        const entry = listeners.get(chart);
        const drag = entry?.state.drag;
        if (!drag || Math.abs(drag.curX - drag.startX) < DRAG_THRESHOLD_PX) return;
        const { ctx, chartArea: a } = chart;
        const left = Math.min(drag.startX, drag.curX);
        const width = Math.abs(drag.curX - drag.startX);
        ctx.save();
        ctx.fillStyle = SELECTION_FILL;
        ctx.fillRect(left, a.top, width, a.bottom - a.top);
        ctx.strokeStyle = SELECTION_EDGE;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(left + 0.5, a.top);
        ctx.lineTo(left + 0.5, a.bottom);
        ctx.moveTo(left + width - 0.5, a.top);
        ctx.lineTo(left + width - 0.5, a.bottom);
        ctx.stroke();
        ctx.restore();
      },

      beforeDestroy(chart) {
        listeners.get(chart)?.detach();
        listeners.delete(chart);
      },
    };
  }

  return { plugin: pluginRef.current, range, isZoomed: range != null, reset };
}
